import Icon from '@/components/Common/Icons/Icon';
import { SvgIcons } from '@/types/types';
import { Tooltip } from '@heroui/tooltip';
import { Link } from '@heroui/link';
import { useRouter, useSearchParams, useParams } from 'next/navigation';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { hideToolSide } from '@/store/slices/toolSideSlice';
import { resetAll } from '@/features/tool/slices/toolSlice';
import React from 'react';
import { isOrganizeTool } from '@/features/tool/helpers/isOrganizeTool';
import { usePlan } from '@/features/subscription/hooks/usePlan';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { formatAnalyticsSource } from '@/helpers/common';
import {
  logHubxEvent,
  APP_EVENTS,
} from '@/lib/analytics/services/log-hubx-event';
import { useUnsavedChangesPrompt } from '@/features/tool/hooks/useUnsavedChangesPrompt';

const ToolMenuCategory = ({
  data,
  isDashboard = false,
  isFooter = false,
}: {
  data: {
    name: string;
    tools: {
      icon: string;
      title: string;
      url?: string;
      tooltip?: string;
      bgColor?: string;
      dashboardUrl?: string;
      isPro?: boolean;
    }[];
  };
  isDashboard?: boolean;
  isFooter?: boolean;
}) => {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const { organizeExportCompleted } = useAppSelector((state) => state.tool);
  const { activeToolSide, toolSideData } = useAppSelector(
    (state) => state.toolSide
  );
  const { planIsActive } = usePlan();
  const t = useTranslations();
  const { guardAction } = useUnsavedChangesPrompt();

  const currentPage = React.useMemo(
    () => `/app/${params.page}?tool=${searchParams.get('tool')}`,
    [params.page, searchParams]
  );

  const currentEffectivePage = React.useMemo(() => {
    if (
      activeToolSide === 'convertDownload' &&
      params.page === 'convert-pdf' &&
      typeof toolSideData?.toolType === 'string'
    ) {
      return `/app/convert-pdf?tool=${toolSideData.toolType}`;
    }

    return currentPage;
  }, [activeToolSide, currentPage, params.page, toolSideData?.toolType]);

  const handleToolClick = (tool: {
    url?: string;
    dashboardUrl?: string;
    title: string;
  }) => {
    if (isDashboard) {
      guardAction(() => {
        const toolId = tool.title.toLowerCase().replace(/\s+/g, '-');
        const targetUrl = tool.dashboardUrl || `/${toolId}`;
        const currentTool = searchParams.get('tool');

        if (
          activeToolSide === 'convertDownload' &&
          targetUrl === currentEffectivePage
        ) {
          dispatch(hideToolSide());
          dispatch(resetAll());
          return;
        }

        if (targetUrl === currentEffectivePage || currentTool === toolId) {
          dispatch(hideToolSide());
          if (
            params.page === 'organize-pdf' &&
            currentTool &&
            isOrganizeTool(t, currentTool) &&
            isOrganizeTool(t, toolId)
          ) {
            if (organizeExportCompleted) {
              dispatch(resetAll());
            }
          } else {
            dispatch(resetAll());
          }
        } else {
          dispatch(hideToolSide());
          if (
            params.page === 'organize-pdf' &&
            currentTool &&
            isOrganizeTool(t, currentTool) &&
            isOrganizeTool(t, toolId)
          ) {
            if (organizeExportCompleted) {
              dispatch(resetAll());
            }
          } else {
            dispatch(resetAll());
          }
          router.push(targetUrl);
        }
      });
    }
  };

  const resolveToolAnalyticsSource = (tool: {
    url?: string;
    dashboardUrl?: string;
    title: string;
  }) => {
    const targetUrl = tool.dashboardUrl || tool.url;

    if (targetUrl) {
      const queryString = targetUrl.split('?')[1];
      if (queryString) {
        const toolParam = new URLSearchParams(queryString).get('tool');
        if (toolParam) {
          return formatAnalyticsSource(toolParam);
        }
      }

      return formatAnalyticsSource(targetUrl);
    }

    return formatAnalyticsSource(tool.title);
  };

  return (
    <div>
      <h3 className="caption font-semibold text-gray-light">{data.name}</h3>
      <div className="flex flex-col gap-3 gap-x-6 mt-5">
        {data.tools.map((tool, index) => (
          <Tooltip
            key={index}
            content={tool.tooltip}
            placement="bottom"
            className="rounded-sm bg-black text-white duration-0"
            offset={10}
            delay={0}
            closeDelay={0}
            motionProps={{
              variants: {
                exit: {
                  opacity: 0,
                  transition: {
                    duration: 0,
                    ease: 'easeIn',
                  },
                },
                enter: {
                  opacity: 1,
                  transition: {
                    duration: 0,
                    ease: 'easeOut',
                  },
                },
              },
            }}
          >
            <Link
              href={
                isDashboard === false
                  ? tool.url !== undefined
                    ? tool.url
                    : `${tool.title.toLowerCase().replace(/\s+/g, '-')}`
                  : tool.dashboardUrl
              }
              key={index}
              className={`rounded-md flex items-center p-2 gap-3 hover:text-white w-full max-w-[200px] duration-0 hover:opacity-100 text-gray-medium font-semibold transition-colors ${tool.bgColor}`}
              onClick={(e) => {
                if (isDashboard) {
                  e.preventDefault();
                  handleToolClick(tool);
                }
                const toolSource = resolveToolAnalyticsSource(tool);
                if (isFooter) {
                  logHubxEvent({
                    eventName: APP_EVENTS.BTN_FOOTER_ALL_TOOLS,
                    source: toolSource,
                  });
                } else {
                  logHubxEvent({
                    eventName: APP_EVENTS.BTN_ALL_TOOLS,
                    source: toolSource,
                  });
                }
              }}
            >
              <Icon name={tool.icon as SvgIcons} width={24} height={24} />
              <span className="body2 !font-semibold">{tool.title}</span>
              {/* {tool.isPro && !planIsActive && (
                <Image
                  src={'/images/global/Pro-Star.svg'}
                  height={15}
                  width={35}
                  alt="pro"
                />
              )} */}
            </Link>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default ToolMenuCategory;
